
import React, { useRef, useMemo, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Group, MathUtils } from 'three';
import { FoliageGroup } from './Foliage';
import { Bauble, Star, FairyLights } from './Decorations';
import { Gift } from './Gifts';
import { DecorationInfo, GiftInfo } from '../types';

export const Tree: React.FC = () => {
  const groupRef = useRef<Group>(null);
  const [introFinished, setIntroFinished] = useState(false);
  const startTime = useRef<number | null>(null);

  const levels = [
    { radius: 3.2, height: 2.5, pos: 0 },
    { radius: 2.5, height: 2.3, pos: 1.6 },
    { radius: 1.8, height: 1.9, pos: 3.2 },
    { radius: 1.1, height: 1.6, pos: 4.5 },
  ];

  const decorations = useMemo(() => {
    const list: DecorationInfo[] = [];
    const colors = ['#f87171', '#60a5fa', '#fbbf24', '#f472b6', '#e2e8f0'];
    
    levels.forEach((level, lIdx) => {
      const count = 10 - lIdx * 2;
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2 + (lIdx * 0.8);
        const r = level.radius * 0.75;
        list.push({
          id: `dec-${lIdx}-${i}`,
          position: [
            Math.cos(angle) * r, 
            level.pos + 0.4 + (Math.random() * (level.height * 0.4)), 
            Math.sin(angle) * r
          ],
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 0.07 + Math.random() * 0.06,
          type: 'bauble'
        });
      }
    });
    return list;
  }, []);

  const gifts: GiftInfo[] = useMemo(() => [
    { id: 'g1', position: [2.0, 0.35, 0.8], color: '#ef4444', rotation: [0, 0.5, 0], size: [0.7, 0.7, 0.7] },
    { id: 'g2', position: [-1.4, 0.3, 2.0], color: '#3b82f6', rotation: [0, -0.4, 0], size: [0.6, 0.6, 0.6] },
    { id: 'g3', position: [0.4, 0.4, -2.2], color: '#10b981', rotation: [0, 1.2, 0], size: [0.8, 0.8, 0.8] },
    { id: 'g4', position: [-2.0, 0.25, -0.8], color: '#f59e0b', rotation: [0, 0.3, 0], size: [0.5, 0.5, 0.5] },
  ], []);

  useFrame((state) => {
    if (!groupRef.current) return;
    
    if (startTime.current === null) startTime.current = state.clock.elapsedTime;
    const elapsedSinceStart = state.clock.elapsedTime - startTime.current;

    if (!introFinished) {
      const duration = 2.5; // 稍微加快动画速度
      const progress = Math.min(elapsedSinceStart / duration, 1);
      const s = MathUtils.lerp(0, 1, MathUtils.smoothstep(progress, 0, 1));
      groupRef.current.scale.set(s, s, s);
      groupRef.current.rotation.y = (1 - progress) * Math.PI;
      groupRef.current.position.y = (1 - progress) * -2;
      if (progress >= 1) setIntroFinished(true);
    } else {
      groupRef.current.scale.set(1, 1, 1);
      groupRef.current.position.y = 0;
    }

    groupRef.current.traverse((child) => {
      if ((child as any).isPointLight) {
        (child as any).intensity = (child as any).name === 'star-light' ? 
          2.5 + Math.sin(state.clock.elapsedTime * 4) * 0.5 : 
          0.5 + Math.sin(state.clock.elapsedTime * 2) * 0.2;
      }
    });
  });

  return (
    <group 
      ref={groupRef} 
      scale={introFinished ? [1, 1, 1] : [0, 0, 0]} 
      position={[0, introFinished ? 0 : -2, 0]}
    >
      <mesh position={[0, 1.5, 0]} castShadow>
        <cylinderGeometry args={[0.2, 0.4, 3.5, 8]} />
        <meshStandardMaterial color="#3d1f05" roughness={1} />
      </mesh>

      {levels.map((level, idx) => (
        <FoliageGroup 
          key={idx}
          level={idx} 
          radius={level.radius} 
          height={level.height} 
          baseHeight={level.pos} 
        />
      ))}

      <FairyLights levels={levels} />
      {decorations.map(dec => (
        <Bauble key={dec.id} info={dec} />
      ))}

      <Star position={[0, 6.1, 0]} />

      {gifts.map(gift => (
        <Gift key={gift.id} info={gift} />
      ))}

      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]} receiveShadow>
        <circleGeometry args={[15, 64]} />
        <meshStandardMaterial color="#050810" transparent opacity={0.4} roughness={1} />
      </mesh>
    </group>
  );
};
